import { createFileRoute, notFound } from "@tanstack/react-router";
import { getPost, getTranslation } from "@/lib/blog";
import { BlogPostLayout } from "@/components/BlogPostLayout";

export const Route = createFileRoute("/es/blog/$slug")({
  loader: ({ params }) => {
    const post = getPost(params.slug, "es");
    if (!post) throw notFound();
    const enPost = getTranslation(post.translationKey, "en");
    return { post, enPost };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    const enPost = loaderData?.enPost;
    const canonicalUrl = `https://adkavi.ai/es/blog/${post?.slug ?? ""}`;
    const enUrl = enPost ? `https://adkavi.ai/blog/${enPost.slug}` : undefined;
    return {
      meta: [
        { title: post ? `${post.title} — AdKavi` : "Blog — AdKavi" },
        { name: "description", content: post?.description ?? "" },
        { property: "og:title", content: post?.title ?? "" },
        { property: "og:description", content: post?.description ?? "" },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "es_ES" },
        { property: "og:url", content: canonicalUrl },
        ...(post?.ogImage ? [{ property: "og:image", content: post.ogImage }] : []),
        ...(post?.ogImage ? [{ name: "twitter:image", content: post.ogImage }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post?.title ?? "" },
        { name: "twitter:description", content: post?.description ?? "" },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
        { rel: "alternate", hreflang: "es", href: canonicalUrl },
        ...(enUrl
          ? [
              { rel: "alternate", hreflang: "en", href: enUrl },
              { rel: "alternate", hreflang: "x-default", href: enUrl },
            ]
          : [{ rel: "alternate", hreflang: "x-default", href: canonicalUrl }]),
      ],
      scripts: post
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                headline: post.title,
                description: post.description,
                datePublished: post.date,
                inLanguage: "es-ES",
                url: canonicalUrl,
                author: { "@type": "Organization", name: "AdKavi", url: "https://adkavi.ai" },
                publisher: {
                  "@type": "Organization",
                  name: "AdKavi",
                  url: "https://adkavi.ai",
                  logo: {
                    "@type": "ImageObject",
                    url: "https://adkavi.ai/favicon.ico",
                  },
                },
                ...(post.ogImage ? { image: post.ogImage } : {}),
              }),
            },
          ]
        : [],
    };
  },
  component: EsBlogPost,
});

function EsBlogPost() {
  const { post, enPost } = Route.useLoaderData();
  return <BlogPostLayout post={post} alternatePost={enPost} lang="es" />;
}
